import React, { useState } from 'react';
import { ShieldCheck, Sparkles, CheckCircle2, ArrowRight } from 'lucide-react';

export default function Apply() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    experience: 'Beginner (0-1 year)',
    capitalSize: '₹1 Lakh - ₹5 Lakhs',
    tradingGoal: 'Build a disciplined, rules-based trading process',
    timeCommitment: '3-4 hours per week'
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');

    try {
      // Relative path for local dev & Hostinger production
      const response = await fetch('/api/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          experience: 'Beginner (0-1 year)',
          capitalSize: '₹1 Lakh - ₹5 Lakhs',
          tradingGoal: 'Build a disciplined, rules-based trading process',
          timeCommitment: '3-4 hours per week'
        });
      } else {
        setErrorMsg(data.message || 'Failed to submit application.');
      }
    } catch (err) {
      console.error('Apply Form Error:', err);
      setErrorMsg('Server connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#FAF8F5] text-[#0B2540] min-h-screen font-sans selection:bg-[#14956A] selection:text-white py-12 px-6">
      <div className="max-w-4xl mx-auto space-y-10">
        
        {/* Page Header */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 bg-[#E6F4F1] border border-[#A3E0D3] text-[#14956A] text-xs font-semibold px-4 py-1.5 rounded-full">
            <Sparkles className="w-4 h-4 text-[#14956A]" />
            Cohort Waitlist & Application
          </div>

          <h1 className="font-serif-title text-4xl sm:text-5xl font-extrabold text-[#0B2540] tracking-tight">
            Apply for <span className="text-[#14956A] italic font-normal">Next Cohort.</span>
          </h1>

          <p className="text-gray-600 text-xs sm:text-sm max-w-xl mx-auto leading-relaxed">
            We limit batch sizes to maintain high interaction and personalized trade reviews. Fill in your details below to secure early access.
          </p>
        </div>

        {/* Application Form Container */}
        <div className="bg-white rounded-3xl border border-gray-200/80 p-8 sm:p-12 shadow-md">
          {submitted ? (
            <div className="text-center py-10 space-y-4">
              <div className="w-16 h-16 bg-[#E6F4ED] text-[#14956A] rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h3 className="font-serif-title text-2xl font-bold text-[#0B2540]">Application Received!</h3>
              <p className="text-xs sm:text-sm text-gray-600 max-w-md mx-auto">
                Thank you for applying. Haritika and the advisory team will review your application and send cohort enrollment details to your email 48 hours prior to public launch.
              </p>
              <button 
                onClick={() => setSubmitted(false)}
                className="mt-4 bg-[#0B2540] text-white px-6 py-2.5 rounded-full text-xs font-bold hover:bg-[#14956A] transition"
              >
                Submit Another Application
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Personal Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    required
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Full Name"
                    className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl px-4 py-3 text-xs sm:text-sm focus:outline-none focus:border-[#14956A] transition"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@email.com"
                    className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl px-4 py-3 text-xs sm:text-sm focus:outline-none focus:border-[#14956A] transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-2">
                    Phone Number (WhatsApp) *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl px-4 py-3 text-xs sm:text-sm focus:outline-none focus:border-[#14956A] transition"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-2">
                    Prior Trading Experience *
                  </label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl px-4 py-3 text-xs sm:text-sm focus:outline-none focus:border-[#14956A] transition"
                  >
                    <option value="Complete Beginner">Complete Beginner (0 months)</option>
                    <option value="Beginner (0-1 year)">Beginner (0-1 year)</option>
                    <option value="Intermediate (1-3 years)">Intermediate (1-3 years)</option>
                    <option value="Experienced (3+ years)">Experienced (3+ years)</option>
                  </select>
                </div>
              </div>

              {/* Capital & Goals */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-2">
                    Planned Capital Allocation *
                  </label>
                  <select
                    name="capitalSize"
                    value={formData.capitalSize}
                    onChange={handleChange}
                    className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl px-4 py-3 text-xs sm:text-sm focus:outline-none focus:border-[#14956A] transition"
                  >
                    <option value="Paper Trading Only">Paper Trading Only (Zero Capital)</option>
                    <option value="< ₹1 Lakh">&lt; ₹1 Lakh</option>
                    <option value="₹1 Lakh - ₹5 Lakhs">₹1 Lakh - ₹5 Lakhs</option>
                    <option value="₹5 Lakhs - ₹20 Lakhs">₹5 Lakhs - ₹20 Lakhs</option>
                    <option value="₹20 Lakhs+">₹20 Lakhs+</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-2">
                    Weekly Time Commitment *
                  </label>
                  <select
                    name="timeCommitment"
                    value={formData.timeCommitment}
                    onChange={handleChange}
                    className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl px-4 py-3 text-xs sm:text-sm focus:outline-none focus:border-[#14956A] transition"
                  >
                    <option value="1-2 hours per week">1-2 hours per week</option>
                    <option value="3-4 hours per week">3-4 hours per week (Recommended)</option>
                    <option value="5+ hours per week">5+ hours per week</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-2">
                  What is your primary goal from this course? *
                </label>
                <textarea
                  name="tradingGoal"
                  rows="3"
                  required
                  value={formData.tradingGoal}
                  onChange={handleChange}
                  placeholder="e.g. I want to learn systematic risk management and stop making emotional trades."
                  className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl p-4 text-xs sm:text-sm focus:outline-none focus:border-[#14956A] transition"
                ></textarea>
              </div>

              {errorMsg && (
                <p className="text-xs font-bold text-red-600 bg-red-50 p-3 rounded-xl border border-red-200">{errorMsg}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#14956A] hover:bg-[#0E6C4C] text-white font-bold py-4 rounded-xl text-xs sm:text-sm transition duration-300 shadow-md shadow-[#14956A]/20 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? 'Submitting Application...' : 'Submit Cohort Application'}
                <ArrowRight className="w-4 h-4" />
              </button>

            </form>
          )}
        </div>

        {/* Bottom Trust Note */}
        <div className="bg-[#E6F4F1] border border-[#A3E0D3] rounded-2xl p-4 flex items-center gap-3 text-xs text-gray-700">
          <ShieldCheck className="w-5 h-5 text-[#14956A] shrink-0" />
          <span>Your data is confidential and strictly used for course evaluation and advisory communication.</span>
        </div>

      </div>
    </div>
  );
}